/** @format */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { FiEye, FiEyeOff } from "react-icons/fi";
import RobotImage from "../../../assets/login_img.png";
import Login_Anim from "../../../assets/com-logo.png";
import { redirectToFirstAccessibleRoute } from "../../../utils/permissionRedirect";
// Import test utilities for development
if (process.env.NODE_ENV === "development") {
  import("../../../utils/permissionRedirectTests").then((testModule) => {
    window.testPermissionRedirect = testModule.testPermissionRedirect;
    window.testMultiplePermissionScenarios =
      testModule.testMultiplePermissionScenarios;
  });
}

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showSplash, setShowSplash] = useState(false);

  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    try {
      const response = await fetch(`${API_URL}/v1/hris/user/userLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: email, password }),
      });

      const data = await response.json();

      if (response.status === 200 && data.success) {
        const { accessToken, refreshToken, user, permissions } = data;
        Cookies.set("user_token", accessToken);
        Cookies.set("refresh_token", refreshToken);
        Cookies.set("username", user.username);
        Cookies.set("employee_fullname", user.name);
        Cookies.set("email", user.email);
        Cookies.set("mobile", user.mobile);
        Cookies.set("user_role", user.role);
        Cookies.set("is_first_login", user.is_first_login);
        Cookies.set("permissions", permissions.join(","));

        // Show full white splash for 3s, then navigate to first accessible route
        setShowSplash(true);

        // Convert permissions array to string array for utility function
        const permissionStrings = permissions.map((p) => p.toString());

        // Redirect to the first accessible route based on user permissions
        redirectToFirstAccessibleRoute(navigate, permissionStrings, 3000);
      } else {
        setLoginError("Invalid credentials or login failed.");
      }
    } catch (error) {
      console.error("Login Error:", error);
      setLoginError("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      {/* Blink animation */}
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.25; }
          }
          .blink {
            animation: blink 1.1s ease-in-out infinite;
          }
        `}
      </style>

      {/* Main layout (rendered normally underneath) */}
      <div className="flex flex-col md:flex-row h-screen w-screen overflow-hidden font-montserrat">
        {/* Left Section */}

        <div className="w-full md:w-1/2 h-[40vh] md:h-full relative overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/grok-video-88dd65ab-82ec-499a-9d5e-2b6c715ac2d8.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>


        {/* Right Section */}
        <div className="w-full md:w-1/2 bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
          <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6">
            {/* Header Section */}
            <div className="text-center space-y-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Welcome Back
              </h2>
              <p className="text-gray-600">
                Sign in to <span className="font-semibold text-[#001F3F]">TalexaHRMS</span>
              </p>
              <p className="text-sm text-gray-500">
                Your all-in-one Human Resource Management Solution
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4 mt-8">
              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="text"
                  placeholder="email@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 
                     focus:outline-none focus:ring-2 focus:ring-[#001F3F] focus:border-transparent
                     transition-all duration-200 bg-white
                     disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={showSplash}
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                 
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-12
                       focus:outline-none focus:ring-2 focus:ring-[#001F3F] focus:border-transparent
                       transition-all duration-200 bg-white
                       disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={showSplash}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-4 flex items-center text-gray-500 
                       hover:text-gray-700 transition-colors"
                    disabled={showSplash}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>

              
            </div>

            {/* Error Message */}
            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm text-center">{loginError}</p>
              </div>
            )}

            {/* Sign In Button */}
            <button
              type="submit"
              className="w-full bg-[#001F3F] hover:bg-[#002955] text-white font-semibold 
                 py-3.5 rounded-lg transition-all duration-200 
                 disabled:opacity-60 disabled:cursor-not-allowed
                 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              disabled={showSplash}
            >
              {showSplash ? "Signing in..." : "Sign In"}
            </button>

            

           
          </form>
        </div>
      </div>

      {/* FULL WHITE SPLASH (covers everything) */}
      {showSplash && (
        <div
          className="fixed inset-0 z-[9999] bg-white flex items-center justify-center"
          aria-live="polite"
          aria-busy="true"
        >
          <img
            src={Login_Anim}
            alt="Signing in..."
            className="blink object-contain
                       w-[80vw] max-w-[580px]
                       h-auto max-h-[90vh]"
          />
        </div>
      )}
    </>
  );
};

export default Login;
